// CleverTap Configuration
// This file contains all CleverTap configuration and initialization logic
//
// CleverTap Enablement Strategy:
// 1. If REACT_APP_CLEVERTAP_ENABLED is explicitly set, use that value
// 2. Otherwise, fallback to production environment check (REACT_APP_PROD=true or NODE_ENV=production)
//
// CleverTap Project Strategy:
// - Development & QA & Pre-Release: Uses TEST CleverTap project (TEST-56R-ZKK-W57Z)
// - Production: Uses LIVE CleverTap project (46R-ZKK-W57Z)
//
// Default Environment Settings:
// - Development: CleverTap ENABLED with TEST project (for safe testing)
// - QA: CleverTap ENABLED with TEST project (for testing validation)
// - Pre-Release: CleverTap ENABLED with TEST project (for staging validation)
// - Production: CleverTap ENABLED with LIVE project (for production analytics)

/**
 * Get the CleverTap account configuration based on environment
 * @returns {Object} CleverTap account configuration
 */
export const getCleverTapAccountConfig = () => {
  // Use environment-specific account ID if provided
  const accountId = process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID;
  const region = process.env.REACT_APP_CLEVERTAP_REGION || 'eu1';
  
  if (accountId) {
    return {
      ACCOUNT_ID: accountId,
      REGION: region,
      PROJECT_TYPE: accountId.startsWith('TEST-') ? 'test' : 'live'
    };
  }
  
  // Fallback to default live configuration
  return {
    ACCOUNT_ID: "46R-ZKK-W57Z",
    REGION: "eu1", 
    PROJECT_TYPE: 'live'
  };
};

/**
 * Check if CleverTap should be enabled
 * Enable CleverTap for Production and QA environments only
 * Development environment has CleverTap disabled by default
 * @returns {boolean} - Whether CleverTap should be enabled
 */
export const isCleverTapEnabled = () => {
  // Check if CleverTap is explicitly enabled via environment variable
  if (process.env.REACT_APP_CLEVERTAP_ENABLED !== undefined) {
    return process.env.REACT_APP_CLEVERTAP_ENABLED === 'true';
  }
  
  // Fallback to original production check
  return process.env.REACT_APP_PROD === 'true' || process.env.NODE_ENV === 'production';
};

// CleverTap Account Configuration
export const CLEVERTAP_CONFIG = {
  ...getCleverTapAccountConfig(),
  DEBUG_LEVEL: process.env.NODE_ENV === 'development' ? 3 : (isCleverTapEnabled() ? 0 : 3), // Set to 0 for production, 3 for development
  TARGET_DOMAIN: "humbee.in", // Your domain for tracking
  ENABLED: isCleverTapEnabled(), // Whether CleverTap is enabled
};

/**
 * Initialize CleverTap SDK configuration
 * This function sets up the basic CleverTap configuration object
 */
export const initializeCleverTapConfig = () => {
  // Skip initialization if CleverTap is disabled
  if (!CLEVERTAP_CONFIG.ENABLED) {
    console.log('CleverTap disabled - not enabled for this environment');
    return;
  }

  // Log which CleverTap project is being used
  console.log(`[CleverTap] Initializing ${CLEVERTAP_CONFIG.PROJECT_TYPE} project: ${CLEVERTAP_CONFIG.ACCOUNT_ID}`);

  // Check if CleverTap is already configured from HTML
  if (window.clevertap && 
      window.clevertap.account && 
      window.clevertap.account.length > 0 &&
      window.clevertap.account.some(config => config && config.id === CLEVERTAP_CONFIG.ACCOUNT_ID)) {
    return;
  }

  // Create the global clevertap object if it doesn't exist
  window.clevertap = window.clevertap || {
    event: [],
    profile: [],
    account: [],
    onUserLogin: [],
    notifications: [],
    privacy: []
  };

  // Clear any existing account configuration to avoid duplicates
  window.clevertap.account = [];
  
  // Configure CleverTap account settings - this MUST be the first thing pushed
  window.clevertap.account.push({ 
    id: CLEVERTAP_CONFIG.ACCOUNT_ID,
    region: CLEVERTAP_CONFIG.REGION
  });

  // Set region directly as well
  window.clevertap.region = CLEVERTAP_CONFIG.REGION;

  // Privacy settings
  window.clevertap.privacy = window.clevertap.privacy || [];
  if (window.clevertap.privacy.length === 0) {
    window.clevertap.privacy.push({ optOut: false });
    window.clevertap.privacy.push({ useIP: true });
  }

  // Set debug level (only for development)
  if (process.env.NODE_ENV === 'development') {
    window.clevertap.setDebugLevel = CLEVERTAP_CONFIG.DEBUG_LEVEL;
  }
};

/**
 * Load CleverTap SDK script
 * This function dynamically loads the CleverTap SDK script
 */
export const loadCleverTapSDK = () => {
  return new Promise((resolve, reject) => {
    // Skip loading if CleverTap is disabled
    if (!CLEVERTAP_CONFIG.ENABLED) {
      console.log('CleverTap SDK loading skipped - not enabled for this environment');
      resolve();
      return;
    }
    // Check if SDK is already loaded
    if (document.querySelector('script[src*="clevertap"]')) {
      // Even if script exists, ensure config is set
      if (!window.clevertap || !window.clevertap.account || window.clevertap.account.length === 0) {
        initializeCleverTapConfig();
      }
      resolve();
      return;
    }

    // Initialize config before loading SDK - this is critical!
    initializeCleverTapConfig();

    // Create and configure the script element
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://d2r1yp2w7bby2u.cloudfront.net/js/clevertap.min.js';
    
    // Handle script load success
    script.onload = () => {
      // Wait a bit for SDK to fully initialize and re-apply config
      setTimeout(() => {
        // Ensure account is set after SDK loads
        if (window.clevertap && (!window.clevertap.account || window.clevertap.account.length === 0)) {
          window.clevertap.account = window.clevertap.account || [];
          window.clevertap.account.push({ 
            id: CLEVERTAP_CONFIG.ACCOUNT_ID 
          });
        }
        
        if (window.clevertap && typeof window.clevertap.getAccountID === 'function') {
          resolve();
        } else {
          resolve(); // Still resolve to continue
        }
      }, 500);
    };

    // Handle script load error
    script.onerror = (error) => {
      reject(error);
    };

    // Add script to document head
    document.head.appendChild(script);
  });
};

/**
 * Check if CleverTap SDK is ready for use
 */
export const isCleverTapReady = () => {
  // If CleverTap is disabled, return false
  if (!CLEVERTAP_CONFIG.ENABLED) {
    return false;
  }

  const hasBasicStructure = !!(
    window.clevertap &&
    window.clevertap.onUserLogin &&
    window.clevertap.event &&
    window.clevertap.profile &&
    window.clevertap.account
  );

  const hasAccountId = window.clevertap && 
    window.clevertap.account && 
    window.clevertap.account.length > 0 &&
    window.clevertap.account.some(config => config && config.id);

  const hasGetAccountMethod = window.clevertap && 
    typeof window.clevertap.getAccountID === 'function';

  const isReady = hasBasicStructure && hasAccountId;
  
  return isReady;
};
